import { Chart, ChartTypes } from "@uicore";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Combobox,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Stack,
  Tooltip,
  Typography,
} from "@altimateai/lego";
import { IconChevronDown, IconEye, IconInfoCircle } from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";
import { useAppState } from "@/modules/app/useAppContext";
import { useCostAttribution } from "@/pages/settings/components/userGroups/useCostAttribution";
import { CostAttributionMode } from "@/helpers/types";
import usePaginate from "@/components/paginate/usePaginate";
import { useQuery } from "@tanstack/react-query";
import {
  getTeamSpendFilters,
  getTeamSpendGraphAndSummaryData,
  getTeamSpendTotalSpendsData,
} from "@/helpers/apis";
import { RelativeComponentLoader } from "@/components/loader";
import { DataTable } from "@/components/tables";
import { parseSortDirection } from "@/components/tables/types";
import { formatCost } from "@/helpers/utils";
import dayjs from "dayjs";
import { Bar } from "recharts";
import { createColorMap } from "@/pages/query/common";
import { FlexListSummary } from "@/components/listSummary";
import {
  GET_TEAM_SPEND_FILTERS,
  GET_TEAM_SPEND_GRAPH_AND_SUMMARY,
  GET_TEAM_TOTAL_SPENDS,
  TeamSpendGraphAndSummary,
  TeamSpendQueryParams,
  TeamSpendTotalSpendItem,
} from "@/types/teamSpend";
import { cn } from "@/lib/utils";
import { CheckColoredIcon } from "@assets/icons";
import { Paginate } from "@/components/paginate";
import { DateRange } from "@/components/dateRange";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import { Link, useSearchParams } from "react-router-dom";
import useUserGroups from "@/pages/settings/components/userGroups/useUserGroups";
import { LocalStorageKey } from "@/pages/settings/components/userGroups/UserGroupSelector";
import { shouldRedirectToTeamSpends } from "@/helpers/tenant";
import { isOwner } from "@/services/auth";
import { useBreadcrumb } from "@/components/breadcrumbs";

export const TeamSpend = () => {
  useBreadcrumb("Team Spends");
  const { currency, datastores: instances } = useAppState();
  const { costAttribution } = useCostAttribution();
  const [searchParams, setSearchParams] = useSearchParams();
  const sortAttribute = searchParams.get("sort_key") || "cost";
  const sortOrder = parseSortDirection(searchParams.get("sort_order"), "desc");
  const { page, setPage, pageSize, updatePageSize } = usePaginate({ size: 20 });
  const [chartData, setChartData] = useState<Record<string, string | number>[]>([]);
  const [sortedTeams, setSortedTeams] = useState<[string, number][]>([]);
  const [selectedLegends, setSelectedLegends] = useState<string[]>([]);
  const [allLegends, setAllLegends] = useState<string[]>([]);
  const [summaryData, setSummaryData] = useState<{ label: string; value: string; info?: string }[]>(
    []
  );
  const [distinctTeams, setDistinctTeams] = useState<string[] | null>(null);
  const [team, setTeam] = useState<string[]>(searchParams.get("teams")?.split(",") ?? []);
  const [instance, setInstance] = useState<string>(searchParams.get("instance") ?? "");
  const [legendsOpen, setLegendsOpen] = useState(false);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const { startDate, endDate, onUpdate } = useDateFromSearchParams(
    dayjs().subtract(28, "day").toDate(),
    dayjs().toDate()
  );
  const { groups } = useUserGroups();

  const getQueryParams = (
    includePages = false,
    includeDates = false,
    includeSort = false,
    includeTeams = false,
    includeInstance = false
  ) => {
    const qp: TeamSpendQueryParams = {};
    if (includeDates) {
      qp["end_date"] = dayjs(endDate).format("YYYY-MM-DD");
      qp["start_date"] = dayjs(startDate).format("YYYY-MM-DD");
    }
    if (includeSort) {
      qp["order_by"] = sortAttribute;
      qp["order_direction"] = sortOrder;
    }
    if (team.length > 0 && includeTeams) {
      qp["teams"] = team;
    }
    if (includePages) {
      qp["page"] = page + 1;
      qp["page_size"] = pageSize;
    }
    if (instance && includeInstance) {
      qp["instance_id"] = instance;
    }
    return qp;
  };

  const setTopTenTeamsLegends = (spends: TeamSpendGraphAndSummary["spend_by_teams"]) => {
    const teamSpendsMap = new Map<string, number>();
    spends.forEach(item => {
      Object.entries(item.team_spends).forEach(([team, spend]) => {
        teamSpendsMap.set(team, (teamSpendsMap.get(team) || 0) + spend);
      });
    });
    const sortedTeams = Array.from(teamSpendsMap.entries()).sort((a, b) => b[1] - a[1]);
    setSortedTeams(sortedTeams);
    if (team.length > 0) {
      setSelectedLegends(sortedTeams.map(([team]) => team));
    } else {
      setSelectedLegends(sortedTeams.slice(0, 10).map(([team]) => team));
    }
    setAllLegends(sortedTeams.map(([team]) => team));
  };

  const { data: graphAndSummaryData, isLoading: isGraphAndSummaryLoading } = useQuery({
    queryKey: [GET_TEAM_SPEND_GRAPH_AND_SUMMARY, startDate, endDate, team, instance],
    queryFn: () => getTeamSpendGraphAndSummaryData(getQueryParams(false, true, false, true, true)),
    enabled: !!distinctTeams,
  });

  // Handle successful graph and summary data fetch (replaces onSuccess callback removed in TanStack Query v5)
  useEffect(() => {
    if (graphAndSummaryData) {
      setChartData(
        graphAndSummaryData.spend_by_teams.map(item => ({
          date: item.date,
          ...item.team_spends,
        }))
      );
      setTopTenTeamsLegends(graphAndSummaryData.spend_by_teams);
      setSummaryData([
        {
          label: "Total Spend",
          value: formatCost(graphAndSummaryData.summary.total_spend ?? "N/A", currency),
        },
        {
          label: "Active Teams",
          value:
            graphAndSummaryData.summary.active_teams != null
              ? String(graphAndSummaryData.summary.active_teams)
              : "N/A",
        },
        {
          label: "Average Daily Spend",
          value: formatCost(graphAndSummaryData.summary.average_daily_spend ?? "N/A", currency),
        },
        {
          label: "Unaccounted Spend",
          value: formatCost(graphAndSummaryData.summary.unaccounted_spend ?? "N/A", currency),
        },
      ]);
    }
  }, [graphAndSummaryData, currency]);

  const { data: totalSpendsData, isLoading: isTotalSpendsLoading } = useQuery({
    queryKey: [
      GET_TEAM_TOTAL_SPENDS,
      startDate,
      endDate,
      sortAttribute,
      sortOrder,
      team,
      instance,
      page,
      pageSize,
    ],
    queryFn: () => getTeamSpendTotalSpendsData(getQueryParams(true, true, true, true, true)),
    enabled: !!distinctTeams,
  });

  const {
    data: filtersData,
    isLoading: isFiltersLoading,
    isError: isFiltersError,
  } = useQuery({
    queryKey: [GET_TEAM_SPEND_FILTERS, instance],
    queryFn: () => getTeamSpendFilters(getQueryParams(false, false, false, false, true)),
  });

  // Handle successful filters data fetch (replaces onSuccess callback removed in TanStack Query v5)
  useEffect(() => {
    if (filtersData) {
      setDistinctTeams(filtersData.teams ?? []);
    }
  }, [filtersData]);

  useEffect(() => {
    if (
      costAttribution?.enabled &&
      costAttribution?.mode === CostAttributionMode.Query &&
      shouldRedirectToTeamSpends() &&
      !isOwner()
    ) {
      setShouldRedirect(true);
    }
  }, [costAttribution]);

  const handleLegendsReset = () => {
    if (team.length > 0) {
      setSelectedLegends(sortedTeams.map(([team]) => team));
    } else {
      setSelectedLegends(sortedTeams.slice(0, 10).map(([team]) => team));
    }
  };

  const handleLegendsChange = (legend: string) => {
    setSelectedLegends(prevState => {
      if (prevState.includes(legend)) {
        return prevState.filter(item => item !== legend);
      } else {
        return [...prevState, legend];
      }
    });
  };

  useEffect(() => {
    if (team.length > 0 || instance) {
      setSearchParams(
        prev => {
          const params = new URLSearchParams(prev);
          if (team.length > 0) params.set("teams", team.join());
          if (instance) params.set("instance", instance);
          return params.toString();
        },
        { replace: true }
      );
    } else {
      setSearchParams(
        prev => {
          const params = new URLSearchParams(prev);
          params.delete("teams");
          params.delete("instance");
          return params.toString();
        },
        { replace: true }
      );
    }
  }, [team, instance, setSearchParams]);

  const handleRedirect = (teamName: string) => {
    const userGroupId = groups.find(group => group.name === teamName)?.id ?? "-1";
    localStorage.setItem(LocalStorageKey, userGroupId);
  };

  const headers = [
    {
      label: "Team",
      key: "team",
      uid: "team",
      render: (item: TeamSpendTotalSpendItem) => {
        if (item.team === "Uncategorized") {
          return <span>{item.team}</span>;
        } else {
          return (
            <Link
              onClick={() => handleRedirect(item.team)}
              to={shouldRedirect ? "/discover" : "/summary"}
              className={"al-text-brand"}
            >
              {item.team}
            </Link>
          );
        }
      },
      id: 0,
      sort: {
        sortSearchKey: "sort_key",
        sortOrderSearchKey: "sort_order",
      },
    },
    {
      label: "Total Spends",
      key: "team_spends",
      uid: "team_spends",
      render: (item: TeamSpendTotalSpendItem) => (
        <span>{formatCost(item.team_spends, currency)}</span>
      ),
      id: 1,
      sort: {
        sortSearchKey: "sort_key",
        sortOrderSearchKey: "sort_order",
        sortParamValue: "cost",
      },
    },
    {
      label: "% of Total",
      key: "percentage_spend",
      uid: "percentage_spend",
      render: (item: TeamSpendTotalSpendItem) => <span>{item.percentage_spend} %</span>,
      id: 2,
      sort: {
        sortSearchKey: "sort_key",
        sortOrderSearchKey: "sort_order",
      },
    },
    {
      label: "% Change",
      key: "percentage_change_from_previous_period",
      uid: "percentage_change_from_previous_period",
      tooltipInfo: "Percentage Cost change compared to the previous period of the same length",
      render: (item: TeamSpendTotalSpendItem) =>
        item.percentage_change_from_previous_period !== null ? (
          <span>{item.percentage_change_from_previous_period} %</span>
        ) : (
          "N/A"
        ),
      id: 3,
      sort: {
        sortSearchKey: "sort_key",
        sortOrderSearchKey: "sort_order",
      },
    },
    {
      label: "Cost Attribution",
      key: "details_link",
      uid: "details_link",
      tooltipInfo:
        costAttribution.mode === CostAttributionMode.Query
          ? "View all Queries Attributed at a team level"
          : "View all Warehouses attributed at a team level",
      render: (item: TeamSpendTotalSpendItem) => {
        const isQueryMode = costAttribution.mode === CostAttributionMode.Query;
        const basePath = isQueryMode ? "/query/all" : "/warehouse";

        // For warehouse navigation only: normalize dates to match backend PERIOD_TO_MODEL_MAPPING (1, 7, 30 days)
        // Query page doesn't have this constraint, so use dates as-is
        let normalizedStartDate = dayjs(startDate);
        let normalizedEndDate = dayjs(endDate);

        if (!isQueryMode) {
          // Only normalize for warehouse page
          const daysDiff = dayjs(endDate).diff(dayjs(startDate), "days");

          if (daysDiff >= 28 && daysDiff < 30) {
            // If period is 28 days, round up to 30 days to match PERIOD_TO_MODEL_MAPPING
            normalizedStartDate = dayjs(endDate).subtract(30, "days");
          }
        }

        // Query page uses snake_case (start_date/end_date)
        // Warehouse page uses camelCase (startDate/endDate)
        const startDateKey = isQueryMode ? "start_date" : "startDate";
        const endDateKey = isQueryMode ? "end_date" : "endDate";

        const params = new URLSearchParams({
          teams: item.team,
          [startDateKey]: normalizedStartDate.toISOString(),
          [endDateKey]: normalizedEndDate.toISOString(),
          navigationSource: "team-spends",
        });
        if (instance) {
          params.set("instance", instance);
        }
        const url = `${basePath}?${params.toString()}`;

        return (
          <Stack justify="center" align="center">
            <Button asChild variant="link" className="al-p-0 al-h-auto">
              <Link to={url} aria-label={`View cost attribution for ${item.team}`}>
                <IconEye className="al-size-5" />
              </Link>
            </Button>
          </Stack>
        );
      },
      id: 4,
    },
  ];

  const colorsMap = useMemo(() => {
    return createColorMap(sortedTeams || [], {
      reservedMappings: { Uncategorized: "#5B6FFA" },
      keyExtractor: item => item[0],
    });
  }, [sortedTeams]);

  // Memoize a compact label for the legend selector so the button width stays consistent.
  // Default to "All Legends" when nothing is selected and truncate long lists to avoid overflow.
  const legendButtonLabel = useMemo(() => {
    if (selectedLegends.length === 0) {
      return "All Legends";
    }

    const joinedLegends = selectedLegends.join(", ");
    if (joinedLegends.length <= 18) {
      return joinedLegends;
    }

    return `${joinedLegends.slice(0, 18)}...`;
  }, [selectedLegends]);

  if (isFiltersLoading) {
    return (
      <>
        <Typography variant="page-heading">Team Spends</Typography>
        <RelativeComponentLoader label={"Loading team spending overview"} />
      </>
    );
  }

  // Handle filters API error or empty state
  // When the filters endpoint fails or returns no teams, we show a minimal UI
  // with "No data available" message instead of rendering the full component.
  // This prevents the component from breaking when team data is unavailable.
  if (isFiltersError) {
    return (
      <Stack direction={"column"} gap={"xl"}>
        <Typography variant="page-heading">Team Spends</Typography>
        <Typography className="al-text-muted-foreground">No data available</Typography>
      </Stack>
    );
  }

  return (
    <Stack direction={"column"} gap={"xl"}>
      <Stack justify={"between"}>
        <Stack align={"center"} gap={"sm"}>
          <Typography variant="page-heading">Team Spends</Typography>
          <Tooltip
            title={
              <Stack direction="column" gap="none">
                <span>Cost attribution is calculated at either the warehouse or query level.</span>
                <span>
                  Currently, we only attribute costs for compute and cloud services; no other costs
                  are included at this time.
                </span>
              </Stack>
            }
          >
            <IconInfoCircle
              size={18}
              className="al-text-muted-foreground al-cursor-help al-mt-1"
              aria-label="Cost attribution information"
            />
          </Tooltip>
        </Stack>
        <Stack align={"center"} gap={"xl"}>
          <Stack direction={"row"} align={"center"} gap={"md"}>
            <Label className={"al-m-0"}>Team:</Label>
            <Combobox
              options={distinctTeams?.map(t => ({ value: t, label: t })) ?? []}
              exactSearch
              value={team}
              onChange={setTeam}
              placeholder="All Teams"
              header="Teams"
              searchPlaceholder="Search team..."
              multiSelect
              showApplyButton
              showClearButton
              popoverContentProps={{ className: "!al-w-72" }}
            />
          </Stack>
          <Stack direction={"row"} align={"center"} gap={"md"}>
            <Label className={"al-m-0"}>Instances:</Label>
            <Combobox
              options={instances?.map(i => ({ value: i.instance_id!, label: i.name })) ?? []}
              value={instance}
              onChange={setInstance}
              placeholder="All Instances"
              header={"Instances"}
              searchPlaceholder="Search instance..."
              showClearButton
              popoverContentProps={{ className: "!al-w-72" }}
            />
          </Stack>
          <Stack direction={"row"} align={"center"} gap={"md"}>
            <Label className={"al-m-0"}>Time Period:</Label>
            <DateRange
              startDate={startDate}
              endDate={endDate}
              maxDate={new Date()}
              availableStaticRanges={["Last 7 days", "Last 28 days"]}
              onDateRangeSelect={onUpdate}
            />
          </Stack>
        </Stack>
      </Stack>
      <FlexListSummary isLoading={isGraphAndSummaryLoading} summary={summaryData} />
      <Card>
        <CardHeader>
          <Stack align={"center"} justify={"between"}>
            <Stack direction={"column"} gap={"none"}>
              <Typography variant={"section-heading"}>Spend by Teams</Typography>
              {team.length === 0 && selectedLegends.length === 10 && (
                <Typography variant={"caption"} size={"sm"} className={"al-text-muted-foreground"}>
                  Showing top ten teams by spend
                </Typography>
              )}
            </Stack>
            <Stack direction={"row"} align={"center"} gap={"md"}>
              <Label className={"al-m-0"}>Legends:</Label>
              <Popover open={legendsOpen} onOpenChange={setLegendsOpen}>
                <PopoverTrigger asChild>
                  <Button
                    size={"sm"}
                    variant="outline"
                    role="combobox"
                    aria-label="Legend selector"
                    aria-expanded={legendsOpen}
                    className="al-w-56 al-justify-between al-py-3 al-px-2 al-overflow-hidden al-text-ellipsis al-whitespace-nowrap"
                  >
                    {legendButtonLabel}
                    <IconChevronDown className="ml-2 h-4 w-4 al-shrink-0 al-opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="!al-w-56 !al-p-0">
                  <Command>
                    <CommandInput placeholder="Search legends..." className="h-9" />
                    <CommandList>
                      <CommandEmpty>No Legend Found.</CommandEmpty>
                      <CommandGroup className={"al-overflow-auto"}>
                        {allLegends?.map(t => (
                          <CommandItem
                            key={t}
                            value={t}
                            onSelect={() => handleLegendsChange(t)} // Prevent deselecting the last option
                            className={"al-cursor-pointer"} // Disable styling for the last option
                          >
                            <CheckColoredIcon
                              className={cn(
                                "al-mr-2 al-size-4",
                                selectedLegends.includes(t) ? "al-opacity-100" : "al-opacity-0"
                              )}
                            />
                            {t}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                    <div className={"al-border-t al-p-2"}>
                      <Button
                        size={"sm"}
                        variant={"outline"}
                        className={"!al-h-8 al-w-full"}
                        onClick={handleLegendsReset}
                      >
                        Reset
                      </Button>
                    </div>
                  </Command>
                </PopoverContent>
              </Popover>
            </Stack>
          </Stack>
        </CardHeader>
        <CardContent className={"al-pb-10"}>
          {isGraphAndSummaryLoading ? (
            <RelativeComponentLoader componentHeight={30} label={"Loading Chart"} />
          ) : (
            <Chart
              title="Team Spend Overview"
              xAxisDataKey="date"
              xAxisLabelFormatter={value => dayjs(value).format("DD MMM")}
              yAxisLabelFormatter={value => formatCost(value, currency)}
              type={ChartTypes.BarChart}
              data={chartData}
              tooltipProps={{
                content: ({ active, payload, label }) => {
                  if (!active || !payload || !payload.length) {
                    return null;
                  }

                  const totalSpend = payload.reduce((acc, item) => acc + item.value, 0);

                  return (
                    <div className="al-bg-white al-border al-p-2 al-rounded-md">
                      <div className="al-font-semibold al-mb-2">
                        {dayjs(label).format("DD MMM")}
                      </div>
                      {payload.map(item => (
                        <Stack justify={"between"} align={"center"} gap={"md"} key={item.name}>
                          <Stack align={"center"}>
                            <span
                              className="al-w-3 al-h-3 al-rounded-full al-mr-2"
                              style={{ backgroundColor: item.color }}
                            ></span>
                            {item.name}:
                          </Stack>
                          <span>{formatCost(item.value, currency)}</span>
                        </Stack>
                      ))}
                      {payload.length !== 1 && (
                        <Stack
                          justify={"between"}
                          className="al-border-t al-border-black al-pt-1 al-mt-1"
                        >
                          <span>Total:</span>
                          <span>{formatCost(totalSpend, currency)}</span>
                        </Stack>
                      )}
                    </div>
                  );
                },
              }}
            >
              {({ selectedLegend }) => {
                const numberOfPoints = chartData.length;
                const barSize = Math.max(20, Math.min(120, 600 / numberOfPoints)); // Dynamically calculate bar size

                return (
                  <>
                    {selectedLegends.length > 0
                      ? selectedLegends.map((d, i) => {
                          return (
                            <Bar
                              key={d}
                              barSize={barSize} // Use dynamically calculated bar size
                              dataKey={d}
                              name={d}
                              fill={colorsMap[d]}
                              stackId="spend"
                              radius={
                                i === selectedLegends.length - 1
                                  ? [5, 5, 0, 0] // Apply radius if all subsequent elements have a value of 0
                                  : [0, 0, 0, 0] // Default rectangle for other bars
                              }
                              hide={Boolean(selectedLegend && selectedLegend !== d)}
                            />
                          );
                        })
                      : allLegends.map((d, i) => {
                          return (
                            <Bar
                              key={d}
                              barSize={barSize} // Use dynamically calculated bar size
                              dataKey={d}
                              name={d}
                              fill={colorsMap[d]}
                              stackId="spend"
                              radius={
                                i === selectedLegends.length - 1
                                  ? [5, 5, 0, 0] // Apply radius if all subsequent elements have a value of 0
                                  : [0, 0, 0, 0] // Default rectangle for other bars
                              }
                              hide={Boolean(selectedLegend && selectedLegend !== d)}
                            />
                          );
                        })}
                  </>
                );
              }}
            </Chart>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <Stack justify={"between"}>
            <Typography variant={"section-heading"}>Team Spending Details</Typography>
          </Stack>
        </CardHeader>
        <CardContent>
          {isTotalSpendsLoading ? (
            <RelativeComponentLoader componentHeight={30} label={"Loading total spends"} />
          ) : (
            <>
              <DataTable
                header={headers}
                onRowClick={() => {}}
                data={{ items: totalSpendsData?.items ?? [] }}
                idKey="team"
                resetPage={() => {}}
                defaultSort={{ sortParamValue: "cost", order: "desc" }}
              />
              <Paginate
                itemCount={totalSpendsData?.total || 0}
                numPages={totalSpendsData?.pages || 0}
                page={page}
                pageSize={pageSize}
                onPageClick={setPage}
                onPageSizeChange={updatePageSize}
                showPageSize
              />
            </>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
};
